import { extractTenantSubdomain } from './tenant-host';

const STAMP_KEY = 'minetech-tenant';
const AUTH_KEY = 'minetech-auth';
const QUERY_CACHE_DB = 'minetech-query-cache';

/**
 * FR3 — Drops any persisted session that belongs to a different workspace than
 * the one being loaded.
 *
 * Separate subdomains are separate origins, so the browser already partitions
 * storage between tenants; this covers what it does not — a workspace renamed
 * onto a subdomain another tenant used to occupy, and any single-origin setup
 * where two tenants share storage.
 *
 * Deliberately imports no store: it runs before React mounts, and pulling the
 * auth store in here would trigger its persist rehydration mid-boot. Removing
 * the keys leaves the app booting exactly as a first-time visitor would.
 */
export function enforceTenantSession(hostname?: string): {
  cleared: boolean;
  tenant: string | null;
} {
  if (typeof window === 'undefined') return { cleared: false, tenant: null };

  const current = extractTenantSubdomain(hostname);

  let previous: string | null = null;
  try {
    previous = window.localStorage.getItem(STAMP_KEY);
  } catch {
    return { cleared: false, tenant: current };
  }

  const stamp = () => {
    try {
      if (current) window.localStorage.setItem(STAMP_KEY, current);
      else window.localStorage.removeItem(STAMP_KEY);
    } catch {
      // A storage failure must not block boot; the guard re-runs next load.
    }
  };

  if (previous === null || previous === current) {
    stamp();
    return { cleared: false, tenant: current };
  }

  try {
    window.localStorage.removeItem(AUTH_KEY);
    window.indexedDB.deleteDatabase(QUERY_CACHE_DB);
  } catch {
    // Best effort; the session is already unusable without the auth blob.
  }
  stamp();
  return { cleared: true, tenant: current };
}
